import React, { useEffect, useState, useMemo } from "react";
import "./style.scss";
import GoBack from "../../../assets/GoBackApp.svg";
import TopUp from "../../../assets/TopUpPink.svg";
import Alert from "../../../assets/Alert.svg";
import { useNavigate, useParams } from "react-router-dom";
import Button from "shared/ui/button";
import { OperationType, OrderType } from "static/types";
import { dashboardStore } from "entities/dashboard";
import { useStore } from "zustand";
import { marketStore } from "entities/market";
import Loading from "widgets/loading";
import Modal from "widgets/auth/modal";
import { useTranslation } from "react-i18next";
import { ResponseCodes } from "shared/api/sircap";
import { toast } from "react-toastify";
import useCanAction from "hooks/useCanAction";
import { PATH_LIST } from "shared/lib/react-router";
import { CURRENCY_FLAGS } from "static/currencies";
import { GetCurrByCode } from "helpers/currencies";
import { fxOrderStore } from "entities/fx-order";
import { useVerificationNavigation } from "hooks/useNavigateVerification";

export const CurrencyOrder = () => {
  const { t } = useTranslation("app");
  const { t: tError } = useTranslation("errors");
  const navigate = useNavigate();
  const { asset_id } = useParams();
  const { loadUserAccounts, user_deposit_accounts } = useStore(dashboardStore);
  const { status } = useVerificationNavigation();
  const {
    isLoading,
    isInitialLoading,
    assetInfo,
    loadAssetInfo,
    openOrder,
    openDealReview,
  } = useStore(marketStore);
  const {
    operation,
    quantity,
    limitPrice,
    change_type,
    amount,
    selectedOfferID,
  } = useStore(fxOrderStore);
  const [confirmDeal, setConfirmDeal] = useState(false);
  const action = useCanAction();

  const [load, setLoad] = useState(false);
  const [dealError, setDealError] = useState(false);
  const [dealData, setDealData] = useState<any>(null);

  const requestDto = useMemo(() => {
    if (!assetInfo?.asset) return null;
    if (!operation || !quantity) return null;

    return {
      pinCode: "",
      comments: "",
      asset: assetInfo.asset.id,
      type: limitPrice ? OrderType.Limit : OrderType.Market,
      price: limitPrice || 0,
      quantity: quantity,
      operation: operation,
      with_sirius: !limitPrice || !!selectedOfferID,
      tif: undefined,
      specific_amount: change_type === "amount" ? +amount : undefined,
      special_offer_id: selectedOfferID || undefined,
    };
  }, [
    assetInfo,
    operation,
    quantity,
    limitPrice,
    amount,
    change_type,
    selectedOfferID,
  ]);

  useEffect(() => {
    if (assetInfo?.asset && requestDto) {
      openDealReview(assetInfo.asset.id, requestDto)
        .then((resp) => {
          if (resp.error) {
            setDealError(true);
            setDealData(null);
          } else {
            setDealError(false);
            setDealData(resp.data.data);
          }
        })
        .catch(() => {
          setDealError(true);
          setDealData(null);
        });
    }
  }, [assetInfo, operation, requestDto]);

  useEffect(() => {
    loadAssetInfo(asset_id);
    loadUserAccounts();
  }, []);

  const handleSell = (pinCode: string) => {
    if (assetInfo?.asset) {
      setLoad(true);
      openOrder(
        assetInfo.asset.id,
        { ...requestDto!, pinCode },
        tError,
        (error?: number) => {
          setLoad(false);
          if (error) {
            if (error === ResponseCodes.WrongPinCode) {
              setDealError(true);
            } else {
              const err = String(error);
              toast.error(tError(err));
            }
          } else {
            toast.success("Success");
            fxOrderStore.getState().clear();
            navigate(PATH_LIST.currencies, {
              state: { currencyTab: "orders" },
            });
          }
        }
      );
    }
  };

  const userBalance = useMemo(() => {
    if (!dealData) return 0;
    const key =
      operation === OperationType.Sell ? dealData.symbol : dealData.currency;
    const acc = user_deposit_accounts.find((acc) => acc.currency.code === key);

    return (acc && +acc?.amount.toFixed(4)) || 0;
  }, [user_deposit_accounts, dealData, operation]);

  if (isInitialLoading || !assetInfo || !dealData) return <Loading />;

  const symbolSymbol = GetCurrByCode(dealData.symbol)?.symbol;
  const currencySymbol = GetCurrByCode(dealData.currency)?.symbol;

  return (
    <div className="currency-order-container">
      <div className="currency-order-header">
        <img
          className="go-back-portfolio"
          onClick={() => navigate(PATH_LIST.currencies)}
          src={GoBack}
          alt="Go Back"
        />
        <p>{t("reviewOrder")}</p>
      </div>

      <div className="currency-order-main">
        <div className="card-header">
          <div>
            <p>
              +{+dealData.exchanged.toFixed(4)}{" "}
              {operation !== OperationType.Buy
                ? dealData.currency
                : dealData.symbol}
            </p>
            <span>
              {t("sell")} {+dealData.amount.toFixed(4)}{" "}
              {operation !== OperationType.Buy
                ? dealData.symbol
                : dealData.currency}
            </span>
          </div>
          <img
            style={{ cursor: "pointer" }}
            src={
              CURRENCY_FLAGS[
                operation === OperationType.Buy
                  ? dealData.symbol
                  : dealData.currency
              ]
            }
            alt="Flag"
          />
        </div>

        <div className="currency-card">
          <div className="detail-row">
            <span>{t("amount")}</span>
            <p>
              {+dealData.amount.toFixed(4)}{" "}
              {operation !== OperationType.Buy ? symbolSymbol : currencySymbol}
            </p>
          </div>
          <div className="detail-row">
            <span>{t("price")}</span>
            <p className="price-highlight" style={{ color: "var(--style2)" }}>
              1{symbolSymbol} = {+dealData.price.toFixed(4)} {currencySymbol}
            </p>
          </div>
          <div className="detail-row">
            <span>{t("exchanged")}</span>
            <p>
              {+dealData.exchanged.toFixed(4)}{" "}
              {operation !== OperationType.Buy ? currencySymbol : symbolSymbol}
            </p>
          </div>
          <div className="detail-row">
            <span>{t("fees")}</span>
            <p>
              {dealData.fees
                ? dealData.fees +
                  " " +
                  currencySymbol +
                  (currencySymbol !== "֏"
                    ? ` (${+dealData.amdCommission.toFixed(2)}֏)`
                    : "")
                : t("noFees")}
            </p>
          </div>
          <div className="detail-row">
            <span>
              {operation === OperationType.Sell
                ? t("estimatedTotalProceeds")
                : t("estimatedTotalCost")}
            </span>
            <p>
              {+dealData.estimatedTotal.toFixed(4)} {currencySymbol}
            </p>
          </div>
        </div>

        <div className="currency-order-btns-wrapper">
          {!dealData.isEnough && (
            <div className="currency-order-btns-wrapper__info-box">
              <img
                style={{ marginBlock: "2px auto" }}
                src={Alert}
                alt="Alert"
              />
              <span>{t("insufficientBalance")}</span>
              <p>
                {userBalance}{" "}
                {operation === OperationType.Sell
                  ? symbolSymbol
                  : currencySymbol}
              </p>
              <img
                style={{ cursor: "pointer" }}
                onClick={() =>
                  status
                    ? toast.error(t("verificationRequired"))
                    : navigate(PATH_LIST.deposit)
                }
                src={TopUp}
                alt="TopUp"
              />
            </div>
          )}

          <Button
            type="submit"
            disabled={!dealData.isEnough || isLoading}
            variant="dark"
            className="customBtn deal-btn"
            onClick={() => action(() => setConfirmDeal(true), "fx")}
          >
            {t("confirm")}
            {/* {t("sell")} {dealData.currency} {t("to")} {dealData.symbol} */}
          </Button>
        </div>
      </div>

      {confirmDeal === true && (
        <Modal
          customModal="modalLogOut"
          onClick={handleSell}
          onHide={() => setConfirmDeal(false)}
          title={t("confirmDeal")}
          text={t("toCompleteDeal")}
          btnText={t("confirm")}
          showSecondBtn={false}
          customOutlet="customOutlet"
          confirmDeal={true}
          isLoading={load}
          dealError={dealError}
        />
      )}
    </div>
  );
};
